import { type HTMLAttributes, type MouseEvent, useMemo } from 'react';

import {
  RICH_TEXT_IDLE_PLACEHOLDER_CLASS,
  RICH_TEXT_PROSE_CLASS,
} from '@/components/editor/inline-rich-text-surface';
import { usePreviewInternalLinkClick } from '@/components/editor/use-preview-internal-link-click';
import { useIsClient } from '@/hooks/use-is-client';
import { useIsPageEditor } from '@/hooks/use-is-page-editor';
import {
  escapeHtml,
  hasRichTextContent,
  type RichTextProfile,
  richTextToPlainText,
  sanitizeRichText,
} from '@/lib/rich-text';
import { cn } from '@/lib/utils';

export type RichTextPreviewMode = RichTextProfile | 'trusted';

export interface RichTextPreviewProps extends HTMLAttributes<HTMLElement> {
  value: string;
  // mode:
  // - 'trusted' → already safe (server or editor); render as-is so SSR keeps markup
  // - 'inline' | 'document' → untrusted; sanitize with that allowlist in the browser
  mode: RichTextPreviewMode;
  as?: 'div' | 'span';
  isEmpty?: boolean;
  placeholder?: string;
  headingsOnly?: boolean;
}

/**
 * Renders rich text content in preview mode (non-editable).
 * Used when editor code is not loaded (public pages) or as fallback.
 */
export function RichTextPreview({
  value,
  mode,
  as: Component = 'div',
  isEmpty: isEmptyProp,
  placeholder,
  headingsOnly = false,
  className,
  ...props
}: RichTextPreviewProps) {
  const isEditor = useIsPageEditor();
  const isClient = useIsClient();
  const resolvedValue = useMemo(() => {
    if (mode === 'trusted') return value;
    if (isClient) return sanitizeRichText(value, mode);

    const text = escapeHtml(richTextToPlainText(value));
    if (text || mode !== 'document') return text;

    // SSR has no DOM sanitizer. Represent textless media with fixed safe markup;
    // never copy source attributes or URLs into the fallback.
    return Array.from(value.matchAll(/<(img|hr)\b[^>]*>/gi), ([, tag]) =>
      tag.toLowerCase() === 'hr' ? '<hr>' : '<span role="img" aria-label="Image">[Image]</span>',
    ).join('');
  }, [value, mode, isClient]);
  const isEmpty = isEmptyProp !== undefined ? isEmptyProp : !hasRichTextContent(resolvedValue);
  const resolvedClassName = cn(RICH_TEXT_PROSE_CLASS, 'tiptap', className);
  const handlePreviewLinkClick = usePreviewInternalLinkClick();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    props.onClick?.(event);
    if (event.defaultPrevented || !(event.target instanceof Element)) return;

    // Keep canvas links from interrupting block selection and editing.
    if (isEditor) {
      if (event.target.closest('a[href]')) event.preventDefault();
      return;
    }

    handlePreviewLinkClick(event);
  };

  if (isEmpty) {
    if (placeholder) {
      type EmptyPlaceholderTag = 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      const PlaceholderTag = (
        headingsOnly ? `h${value.match(/^\s*<h([1-6])\b/i)?.[1] ?? '1'}` : 'p'
      ) as EmptyPlaceholderTag;
      return (
        <Component className={resolvedClassName} {...props}>
          <PlaceholderTag className={RICH_TEXT_IDLE_PLACEHOLDER_CLASS} aria-hidden="true">
            {placeholder}
          </PlaceholderTag>
        </Component>
      );
    }
    return null;
  }

  return (
    <Component
      className={resolvedClassName}
      {...props}
      onClick={handleClick}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Sanitized HTML, trusted HTML, or escaped SSR fallback
      // biome-ignore lint/style/useNamingConvention: React API requires __html
      dangerouslySetInnerHTML={{ __html: resolvedValue }}
    />
  );
}
